import { APP_PAGE_HEADER_STICKY } from '@/constants/config';
import { AppImage } from '@/components/Images';
import { BUTTON_LABELS } from '@/constants/labels';
import { cn } from '@/utils/cn';
import { PopoverButton } from './subcomponents/PopoverButton';
import { PopoverContentDev } from './subcomponents/PopoverContentDev';
import { PopoverContentProfile } from './subcomponents/PopoverContentProfile';
import { PopoverContentSupport } from './subcomponents/PopoverContentSupport';
import { PopoverMenu } from '@/components/Overlays';
import { useCurrentUser } from '@/store/global-store';
import React, { useState } from 'react';

/**
 * Type representing the active menu in the PageHeader.
 */
type TActiveMenu = 'support' | 'profile' | 'developer' | null;

/** Props for the PageHeader component */
export interface PageHeaderProps {
  /** The current route or page identifier */
  route?: string;
  /** Whether the header should be sticky */
  sticky?: boolean;
}

/**
 * PageHeader component displays the header for a page.
 * It includes a logo and can be configured to be sticky.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  route,
  sticky = APP_PAGE_HEADER_STICKY,
}) => {
  const [active, setActive] = useState<TActiveMenu>(null);
  const currentUser = useCurrentUser();

  /**
   * Class names for the header element, conditionally applying sticky
   * styles based on the `sticky` prop.
   */
  const classNames = cn(
    'border-b border-neutral-gray-400',
    'flex flex-row items-center justify-between',
    'px-2 pb-3 pt-0',
    'mb-7',
    'w-full',
    'z-50 top-0',
    sticky ? 'sticky bg-neutral-gray-000 pt-3' : 'relative',
  );

  /**
   * Handler function that toggles the active state for the support and profile buttons. If the same
   * button is clicked again, it will deactivate the menu by setting active to null. This function
   * is used to control which popover menu is currently open.
   */
  const handleButtonClick = (menu: TActiveMenu) => {
    setActive((prev) => (prev === menu ? null : menu));
  };

  /**
   * Array of objects representing the popover menu items for support resources,
   * user profile, and developer options. Each object contains properties such as
   * ariaLabel, content, iconLeft, key, show, and title. The show property is used
   * to conditionally render the developer options based on the user's role.
   */
  const popoverMenuItems = [
    {
      ariaLabel: BUTTON_LABELS.APP_SUPPORT_RESOURCES,
      content: <PopoverContentSupport />,
      iconLeft: 'Notebook' as const,
      key: 'support' as const,
      show: true,
      title: BUTTON_LABELS.APP_SUPPORT_RESOURCES,
    },
    {
      ariaLabel: BUTTON_LABELS.USER_PROFILE,
      content: <PopoverContentProfile />,
      iconLeft: 'User' as const,
      key: 'profile' as const,
      show: true,
      title: BUTTON_LABELS.USER_PROFILE,
    },
    {
      ariaLabel: 'Developer options',
      content: <PopoverContentDev userUuid={currentUser?.uuid} />,
      iconLeft: 'Code' as const,
      key: 'developer' as const,
      show: currentUser?.role === 'developer',
      title: 'Developer options',
    },
  ];

  // =========================
  // Render
  // =========================
  return (
    <header className={classNames} data-route={route}>
      <div>
        <div className="flex w-full items-start justify-start">
          <div className="flex shrink-0 items-center justify-center">
            <AppImage
              src="/logos/fieldone_logo.svg"
              alt="FieldOne Logo"
              width={117.005}
              height={35.175}
            />
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-end gap-3">
          {popoverMenuItems.map((item) =>
            item.show ? (
              <div className="relative" key={item.key}>
                <PopoverMenu
                  activateOnClick={true}
                  content={item.content}
                  onClose={() => handleButtonClick(null)}
                >
                  <PopoverButton
                    active={active === item.key}
                    ariaLabel={item.ariaLabel}
                    iconLeft={item.iconLeft}
                    onClick={() => handleButtonClick(item.key)}
                    title={item.title}
                  />
                </PopoverMenu>
              </div>
            ) : null,
          )}
        </div>
      </div>
    </header>
  );
};

PageHeader.displayName = 'PageHeader';

export default PageHeader;
